import { useReorderSavedSlidesMutation } from "@api/slides-library";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  EmptyState,
  SavedSkeleton,
  useSlidesLibraryStore,
} from "@modules/slides-library/shared";
import { IconGripVertical } from "@tabler/icons-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import type { TSavedSlide } from "@/shared/types";
import { useGetSavedSlides } from "../hooks";
import { SingleSlide } from "./single-slide";

const SortableSlide = memo(function SortableSlide({
  slide,
}: {
  slide: TSavedSlide;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({ id: slide._id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
      }}
      className="tw-group/slide tw-relative tw-min-w-0"
    >
      <SingleSlide slide={slide} />
      <button
        type="button"
        {...attributes}
        {...listeners}
        ref={setActivatorNodeRef}
        aria-label={`Reorder ${slide.title}`}
        className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-[3] tw-flex tw-size-7 tw-shrink-0 tw-cursor-grab tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-bg-white/90 tw-p-0 tw-text-blue-500 tw-opacity-0 tw-shadow-xs tw-outline-none tw-transition-opacity hover:tw-bg-blue-50 focus-visible:tw-pointer-events-auto focus-visible:tw-opacity-100 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200 active:tw-cursor-grabbing group-focus-within/slide:tw-pointer-events-auto group-focus-within/slide:tw-opacity-100 group-hover/slide:tw-pointer-events-auto group-hover/slide:tw-opacity-100"
      >
        <IconGripVertical className="tw-size-4" stroke={1} />
      </button>
    </div>
  );
});

export const SavedSlidesGrid = memo(function SavedSlidesGrid() {
  const { data, isLoading, isPopular } = useGetSavedSlides();
  const type = useSlidesLibraryStore((s) => s.type);
  const search = useSlidesLibraryStore((s) => s.search);
  const [reorderSavedSlides, { isLoading: isReordering }] =
    useReorderSavedSlidesMutation();

  const [localSlides, setLocalSlides] = useState<TSavedSlide[]>(data ?? []);
  useEffect(() => {
    setLocalSlides(data ?? []);
  }, [data]);

  const title = useMemo(
    () => (type === "team-slides" ? "Team slides" : "My slides"),
    [type],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Reorder is disabled while searching (the list is filtered, not the
  // canonical folder order) and in the "Most popular" smart-folder view (a
  // cross-folder aggregate — persisting its order would rewrite the slides'
  // order inside their home folders).
  const hasSortableLayout = !search && !isPopular && localSlides.length > 1;

  // Monotonic drag counter: only the most recent drag may roll back, so a
  // failed earlier request can't wipe out a later drag's persisted order.
  const dragSeqRef = useRef(0);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localSlides.findIndex((s) => s._id === active.id);
    const newIndex = localSlides.findIndex((s) => s._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const prev = localSlides;
    const next = arrayMove(prev, oldIndex, newIndex);
    const seq = ++dragSeqRef.current;
    setLocalSlides(next);
    reorderSavedSlides({ slideIds: next.map((s) => s._id) })
      .unwrap()
      .catch(() => {
        if (dragSeqRef.current === seq) setLocalSlides(prev);
        toast.error("Failed to save the new slide order");
      });
  };

  return (
    <div>
      <div className="tw-flex tw-h-[52px] tw-flex-row tw-items-start tw-justify-between tw-py-4">
        <span className="tw-font-sans tw-text-[14px] tw-font-medium tw-leading-5 tw-text-blue">
          {title}
        </span>
      </div>
      {/* Skeleton only on the initial load — background refetches (e.g. after
          a reorder) keep showing localSlides so there's no blink. */}
      {isLoading ? (
        <div className="tw-grid tw-grid-cols-3 tw-gap-6">
          <SavedSkeleton.Slides />
        </div>
      ) : !localSlides.length ? (
        <EmptyState variant="empty" />
      ) : hasSortableLayout ? (
        <DndContext
          // Keep the sortable/card tree mounted while the request is pending;
          // disabling only the sensors prevents another drag without remounting
          // the expensive slide previews.
          sensors={isReordering ? [] : sensors}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={localSlides.map((s) => s._id)}>
            <div className="tw-grid tw-w-full tw-grid-cols-3 tw-content-start tw-gap-6 tw-overflow-hidden">
              {localSlides.map((slide) => (
                <SortableSlide key={slide._id} slide={slide} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="tw-grid tw-grid-cols-3 tw-gap-6">
          {localSlides.map((slide) => (
            <SingleSlide key={slide._id} slide={slide} />
          ))}
        </div>
      )}
    </div>
  );
});
